/**
 * Click & Collect Widget Web Component
 * Displays store availability and stock levels for the current product variant
 */

if (!customElements.get('click-collect-widget')) {
  class ClickCollectWidget extends HTMLElement {
    constructor() {
      super();
      
      // Bind methods
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handlePostcodeUpdate = this.handlePostcodeUpdate.bind(this);
      this.handleSetStore = this.handleSetStore.bind(this);
      
      // Configuration
      this.apiEndpoint = 'https://flex-fulfillment-api-production.up.railway.app/locations';
      this.geolocationService = 'https://ipapi.co/json/';
      this.cache = new Map();
      this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
      this.requestTimeout = 10000; // 10 seconds
      
      // State
      this.currentVariantId = null;
      this.currentPostcode = null;
      this.isLoading = false;
      this.abortController = null;
      this.lastRequestTime = 0;
      this.minRequestInterval = 1000; // Minimum 1 second between requests
    }

    connectedCallback() {
      this.render();
      this.initializeEventListeners();
      
      // Initialize variant ID from data attribute
      const variantId = this.dataset.variantId;
      if (variantId && variantId !== '') {
        this.currentVariantId = variantId;
      }
      
      
      // Set initial widget state - check if we already have location data
      const storedPostcode = localStorage.getItem('flex_user_postcode');
      if (storedPostcode) {
        this.updateWidgetState('has-location');
      } else {
        this.updateWidgetState('no-location');
      }
      
      this.detectUserLocation();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    /**
     * Initialize event listeners
     */
    initializeEventListeners() {
      // Listen for variant changes
      document.addEventListener('on:variant:change', this.handleVariantChange);
      
      // Postcode update button
      const updateBtn = this.querySelector('.click-collect-widget__update-btn');
      if (updateBtn) {
        updateBtn.addEventListener('click', this.handlePostcodeUpdate);
      }
      
      // Postcode input enter key
      const postcodeInput = this.querySelector('.click-collect-widget__postcode-input');
      if (postcodeInput) {
        postcodeInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            this.handlePostcodeUpdate();
          }
        });
      }
      
    }

    /**
     * Clean up event listeners and abort requests
     */
    cleanup() {
      document.removeEventListener('on:variant:change', this.handleVariantChange);
      if (this.abortController) {
        this.abortController.abort();
      }
      
      // Remove any existing modal from body
      const existingModal = document.getElementById('click-collect-modal');
      if (existingModal) {
        existingModal.remove();
      }
      
      // Restore body overflow
      document.body.style.overflow = '';
    }

    /**
     * Handle variant change events
     */
    handleVariantChange(event) {
      const { variant } = event.detail;
      if (variant && variant.id) {
        this.currentVariantId = `gid://shopify/ProductVariant/${variant.id}`;
        if (this.currentPostcode) {
          this.fetchStoreAvailability();
        }
      }
    }

    /**
     * Handle postcode update
     */
    async handlePostcodeUpdate() {
      const postcodeInput = this.querySelector('.click-collect-widget__postcode-input');
      const newPostcode = postcodeInput?.value?.trim();
      
      if (!newPostcode) {
        this.showError('Please enter a valid postcode');
        return;
      }
      
      // Basic Australian postcode validation
      if (!/^\d{4}$/.test(newPostcode)) {
        this.showError('Please enter a valid 4-digit Australian postcode');
        return;
      }
      
      this.currentPostcode = newPostcode;
      localStorage.setItem('flex_user_postcode', newPostcode);
      
      // Update widget state to show stores view
      this.updateWidgetState('has-location');
      
      if (this.currentVariantId) {
        await this.fetchStoreAvailability();
      }
    }

    /**
     * Handle set store button clicks
     */
    handleSetStore(event) {
      const storeId = event.target.dataset.storeId;
      const storeName = event.target.dataset.storeName;
      
      if (storeId && storeName) {
        localStorage.setItem('flex_preferred_store', JSON.stringify({
          id: storeId,
          name: storeName,
          timestamp: Date.now()
        }));
        
        // Update button text to show selection
        event.target.textContent = 'Selected';
        event.target.disabled = true;
        
        // Reset other buttons
        this.querySelectorAll('.click-collect-widget__set-store-btn').forEach(btn => {
          if (btn !== event.target) {
            btn.textContent = 'Set as your store';
            btn.disabled = false;
          }
        });
      }
    }

    /**
     * Detect user location using browser geolocation 
     */
    async detectUserLocation() {
      try {
        // Check for stored postcode first
        const storedPostcode = localStorage.getItem('flex_user_postcode');
        if (storedPostcode) {
          this.currentPostcode = storedPostcode;
          this.updatePostcodeInput(storedPostcode);
          
          // Auto-fetch if we have both postcode and variant ID
          if (this.currentVariantId) {
            this.fetchStoreAvailability();
          } else {
            // We have location but no variant yet
            this.updateWidgetState('has-location');
          }
          return;
        }

        // Try browser geolocation first
        if (navigator.geolocation) {
          this.requestBrowserLocation();
        } else {
          // Fallback to IP geolocation if browser doesn't support it
          this.fallbackToIPLocation();
        }
        
      } catch (error) {
        // Final fallback - stay in no-location state so user can enter manually
        this.updateWidgetState('no-location');
      }
    }

    /**
     * Request browser location permission
     */
    requestBrowserLocation() {
      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        (position) => this.handleGeolocationSuccess(position),
        (error) => this.handleGeolocationError(error),
        options
      );
    }

    /**
     * Handle successful geolocation
     */
    async handleGeolocationSuccess(position) {
      try {
        const { latitude, longitude } = position.coords;
        
        // Reverse geocode to get postcode
        const postcode = await this.reverseGeocode(latitude, longitude);
        
        if (postcode) {
          this.currentPostcode = postcode;
          this.updatePostcodeInput(postcode);
          localStorage.setItem('flex_user_postcode', postcode);
          this.updateWidgetState('has-location');
          
          // Auto-fetch if we have variant ID
          if (this.currentVariantId) {
            this.fetchStoreAvailability();
          }
        } else {
          // Couldn't get postcode, fallback to IP location
          this.fallbackToIPLocation();
        }
        
      } catch (error) {
        this.fallbackToIPLocation();
      }
    }

    /**
     * Handle geolocation errors
     */
    handleGeolocationError(error) {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          // User denied location access - fallback to IP or manual entry
          this.fallbackToIPLocation();
          break;
        case error.POSITION_UNAVAILABLE:
        case error.TIMEOUT:
          // Location unavailable - fallback to IP
          this.fallbackToIPLocation();
          break;
        default:
          // Unknown error - stay in manual entry mode
          this.updateWidgetState('no-location');
          break;
      }
    }

    /**
     * Reverse geocode coordinates to postcode
     */
    async reverseGeocode(lat, lon) {
      try {
        const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`, {
          headers: { 'Accept': 'application/json' }
        });
        
        if (!response.ok) throw new Error('Reverse geocoding failed');
        
        const data = await response.json();
        
        // Return Australian postcode if available
        if (data.countryCode === 'AU' && data.postcode) {
          return data.postcode;
        }
        
        return null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Fallback to IP-based location detection
     */
    async fallbackToIPLocation() {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);
        
        const response = await fetch(this.geolocationService, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) throw new Error('IP geolocation service unavailable');
        
        const data = await response.json();
        
        if (data.country_code === 'AU' && data.postal) {
          this.currentPostcode = data.postal;
          this.updatePostcodeInput(data.postal);
          localStorage.setItem('flex_user_postcode', data.postal);
          this.updateWidgetState('has-location');
          
          // Auto-fetch if we have variant ID
          if (this.currentVariantId) {
            this.fetchStoreAvailability();
          }
        } else {
          // Stay in no-location state for manual entry
          this.updateWidgetState('no-location');
        }
        
      } catch (error) {
        // Stay in no-location state for manual entry
        this.updateWidgetState('no-location');
      }
    }

    /**
     * Update postcode input field
     */
    updatePostcodeInput(postcode) {
      const input = this.querySelector('.click-collect-widget__postcode-input');
      if (input) {
        input.value = postcode;
      }
    }


    /**
     * Update widget display state
     */
    updateWidgetState(state) {
      // Remove all state classes
      this.classList.remove('widget-state-no-location', 'widget-state-has-location');
      
      // Add current state class
      this.classList.add(`widget-state-${state}`);
    }

    /**
     * Fetch store availability from API
     */
    async fetchStoreAvailability() {
      if (!this.currentVariantId || !this.currentPostcode) return;
      
      // Rate limiting - prevent rapid successive requests
      const now = Date.now();
      if (now - this.lastRequestTime < this.minRequestInterval) {
        return;
      }
      this.lastRequestTime = now;
      
      const cacheKey = `${this.currentVariantId}_${this.currentPostcode}`;
      
      // Check cache first
      if (this.cache.has(cacheKey)) {
        const cached = this.cache.get(cacheKey);
        if (Date.now() - cached.timestamp < this.cacheTimeout) {
          this.renderStores(cached.data);
          return;
        }
      }
      
      this.setLoadingState(true);
      
      try {
        // Abort previous request if still pending
        if (this.abortController) {
          this.abortController.abort();
        }
        
        this.abortController = new AbortController();
        const timeoutId = setTimeout(() => this.abortController.abort(), this.requestTimeout);
        
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            postcode: this.currentPostcode,
            variant_id: this.currentVariantId
          }),
          signal: this.abortController.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Cache the response
        this.cache.set(cacheKey, {
          data: data,
          timestamp: Date.now()
        });
        
        this.renderStores(data);
        
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        
        // Show existing no stores message instead of hiding
        this.showWidget();
        this.updateWidgetState('has-location');
        const storesContainer = this.querySelector('.click-collect-widget__main-store');
        if (storesContainer) {
          storesContainer.innerHTML = `<div class="click-collect-widget__no-stores">No stores found for this product</div>`;
        }
        
      } finally {
        this.setLoadingState(false);
        this.abortController = null;
      }
    }

    /**
     * Set loading state
     */
    setLoadingState(loading) {
      this.isLoading = loading;
      const loadingEl = this.querySelector('.click-collect-widget__loading');
      const storesEl = this.querySelector('.click-collect-widget__stores');
      const updateBtn = this.querySelector('.click-collect-widget__update-btn');
      if (loadingEl) {
        loadingEl.style.display = loading ? 'flex' : 'none';
      }
      
      if (storesEl) {
        storesEl.style.display = loading ? 'none' : 'block';
      }
      
      if (updateBtn) {
        updateBtn.disabled = loading;
      }
    }

    /**
     * Show error message
     */
    showError(message) {
      const errorEl = this.querySelector('.click-collect-widget__error');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.style.display = 'block';
        setTimeout(() => {
          errorEl.style.display = 'none';
        }, 5000);
      }
    }

    /**
     * Hide the entire widget
     */
    hideWidget() {
      this.setAttribute('hidden', '');
    }

    /**
     * Show the widget
     */
    showWidget() {
      this.removeAttribute('hidden');
    }


    /**
     * Render stores list - show only primary store with "View all stores" link
     */
    renderStores(data) {
      if (!data || !data.locations || data.locations.length === 0) {
        // For testing, show a detailed message
        const storesContainer = this.querySelector('.click-collect-widget__main-store');
        if (storesContainer) {
          let message = 'No stores found for this product';
          if (data && data.user_postcode) {
            message += ` in postcode ${data.user_postcode}`;
          }
          if (data && data.user_coords) {
            message += ` (${data.user_coords.state})`;
          }
          storesContainer.innerHTML = `<div class="click-collect-widget__no-stores">${message}</div>`;
        }
        this.updateWidgetState('has-location');
        return;
      }
      
      this.showWidget();
      
      // Store all locations for modal
      this.allLocations = data.locations;
      this.userPostcode = data.user_postcode;
      this.userCoords = data.user_coords;
      
      const preferredStore = JSON.parse(localStorage.getItem('flex_preferred_store') || '{}');
      
      // Sort locations by distance (closest first)
      const sortedLocations = [...data.locations].sort((a, b) => {
        return parseFloat(a.distance_km) - parseFloat(b.distance_km);
      });
      
      
      // Determine primary store (preferred or closest)
      let primaryStore;
      if (preferredStore.id) {
        primaryStore = sortedLocations.find(location => location.id === preferredStore.id);
      }
      if (!primaryStore) {
        primaryStore = sortedLocations[0]; // Closest store
      }
      
      
      // Update widget state to show stores
      this.updateWidgetState('has-location');
      
      // Render primary store - always show "View all stores" for additional details
      this.renderPrimaryStore(primaryStore, true);
    }

    /**
     * Render the primary store display
     */
    renderPrimaryStore(store, hasMultipleStores) {
      const mainStoreContainer = this.querySelector('.click-collect-widget__main-store');
      if (!mainStoreContainer) return;
      
      const stockStatus = this.getStockStatus(store.stock_status);
      const preferredStore = JSON.parse(localStorage.getItem('flex_preferred_store') || '{}');
      const isPreferred = preferredStore.id === store.id;
      
      // Check for next nearest store with stock if primary store is out of stock
      const nextNearestStore = this.getNextNearestStoreWithStock(store);
      
      mainStoreContainer.innerHTML = `
        <div class="click-collect-widget__primary-store">
          <div class="click-collect-widget__store-header">
            <div class="click-collect-widget__store-details">
              <h4 class="click-collect-widget__store-name">${this.escapeHtml(store.name)}</h4>
              <div class="click-collect-widget__store-contact">
                <p class="click-collect-widget__store-address">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-map-pin" aria-hidden="true" focusable="false" role="presentation">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  ${this.escapeHtml(store.address.address1)}, ${this.escapeHtml(store.address.city)}
                </p>
                ${store.address.phone ? `
                  <p class="click-collect-widget__store-phone">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-phone" aria-hidden="true" focusable="false" role="presentation">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <a href="tel:${store.address.phone}" class="click-collect-widget__phone-link">${this.formatPhoneNumber(store.address.phone)}</a>
                  </p>
                ` : ''}
              </div>
            </div>
          </div>
          
          <div class="click-collect-widget__availability">
            <div class="click-collect-widget__availability-row">
              <div class="click-collect-widget__availability-labels">
                <span class="click-collect-widget__availability-label">
                  ${this.getStockIcon(stockStatus.class)}
                  Click & Collect
                </span>
                <span class="click-collect-widget__availability-label">
                  ${this.getStockIcon(stockStatus.class)}
                  In-Store
                </span>
              </div>
              <span class="click-collect-widget__stock-status click-collect-widget__stock-status--${stockStatus.class}">
                ${stockStatus.text}
              </span>
            </div>
          </div>
          
          ${nextNearestStore ? `
            <div class="click-collect-widget__next-nearest">
              <button class="click-collect-widget__next-nearest-text" type="button" aria-label="View details for ${this.escapeHtml(nextNearestStore.name)}">
                <svg class="stock-icon stock-icon--available" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="20,6 9,17 4,12"></polyline>
                </svg>
                Next Closest Store: ${this.escapeHtml(nextNearestStore.name)}
              </button>
            </div>
          ` : ''}
          
        </div>
      `;
      
      // Add "View all stores" button to header if multiple stores
      const headerActions = this.querySelector('.click-collect-widget__header-actions');
      if (headerActions && hasMultipleStores) {
        headerActions.innerHTML = `
          <button class="click-collect-widget__view-all-btn" type="button">
            View all stores
          </button>
        `;
        
        const viewAllBtn = headerActions.querySelector('.click-collect-widget__view-all-btn');
        if (viewAllBtn) {
          viewAllBtn.addEventListener('click', () => this.openStoresModal());
        }
      } else if (headerActions) {
        headerActions.innerHTML = '';
      }
      
      // Add event listener for next nearest store button
      const nextNearestBtn = this.querySelector('.click-collect-widget__next-nearest-text');
      if (nextNearestBtn) {
        nextNearestBtn.addEventListener('click', () => this.openStoresModal());
      }
    }

    /**
     * Get the next nearest store with stock if primary store is out of stock
     */
    getNextNearestStoreWithStock(primaryStore) {
      // Only show next nearest if primary store is out of stock
      const primaryStockStatus = this.getStockStatus(primaryStore.stock_status);
      if (primaryStockStatus.class !== 'out-of-stock') {
        return null;
      }
      
      // Must have all locations and user coordinates to determine same state
      if (!this.allLocations || !this.userCoords) {
        return null;
      }
      
      // Sort all locations by distance
      const sortedLocations = [...this.allLocations].sort((a, b) => {
        return parseFloat(a.distance_km) - parseFloat(b.distance_km);
      });
      
      // Find the next nearest store that:
      // 1. Is not the primary store
      // 2. Has stock (in-stock or low-stock)
      // 3. Is in the same state
      for (const location of sortedLocations) {
        if (location.id === primaryStore.id) continue; // Skip primary store
        
        const stockStatus = this.getStockStatus(location.stock_status);
        const hasStock = stockStatus.class === 'in-stock' || stockStatus.class === 'low-stock';
        
        // For now, assume all stores in the response are in the same state
        // When API provides state info per store, we can add: location.address.state === this.userCoords.state
        if (hasStock) {
          return location;
        }
      }
      
      return null; // No stores with stock found
    }

    /**
     * Open the stores modal
     */
    openStoresModal() {
      if (!this.allLocations) return;
      
      // Create modal at body level for proper z-index stacking
      this.createModal();
      
      const modal = document.getElementById('click-collect-modal');
      if (!modal) return;
      
      // Update modal location info and postcode input
      const locationEl = modal.querySelector('.modal-user-location');
      const postcodeInput = modal.querySelector('.click-collect-widget__modal-postcode-field');
      
      if (locationEl && this.userCoords) {
        locationEl.textContent = `${this.userPostcode} (${this.userCoords.state})`;
      }
      
      if (postcodeInput && this.currentPostcode) {
        postcodeInput.value = this.currentPostcode;
      }
      
      // Render all stores in modal
      this.renderModalStores();
      
      // Show modal
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      
      // Add event listeners
      const closeBtn = modal.querySelector('.click-collect-widget__modal-close');
      const backdrop = modal.querySelector('.click-collect-widget__modal-backdrop');
      const modalUpdateBtn = modal.querySelector('.click-collect-widget__modal-update-btn');
      
      const closeModal = () => this.closeStoresModal();
      
      if (closeBtn) closeBtn.addEventListener('click', closeModal);
      if (backdrop) backdrop.addEventListener('click', closeModal);
      
      // Modal postcode update
      if (modalUpdateBtn) {
        modalUpdateBtn.addEventListener('click', () => this.handleModalPostcodeUpdate());
      }
      
      // Modal postcode input enter key
      if (postcodeInput) {
        postcodeInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            this.handleModalPostcodeUpdate();
          }
        });
      }
      
      // ESC key to close
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          closeModal();
          document.removeEventListener('keydown', handleEsc);
        }
      };
      document.addEventListener('keydown', handleEsc);
    }

    /**
     * Create modal at document body level
     */
    createModal() {
      // Remove existing modal if it exists
      const existingModal = document.getElementById('click-collect-modal');
      if (existingModal) {
        existingModal.remove();
      }
      
      // Create modal HTML
      const modalHtml = `
        <div class="click-collect-widget__modal" id="click-collect-modal" role="dialog" aria-labelledby="stores-modal-title" aria-modal="true">
          <div class="click-collect-widget__modal-backdrop"></div>
          <div class="click-collect-widget__modal-content">
            <div class="click-collect-widget__modal-header">
              <h3 id="stores-modal-title">Check store availability</h3>
              <button class="click-collect-widget__modal-close" type="button" aria-label="Close modal">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div class="click-collect-widget__modal-body">
              <div class="click-collect-widget__modal-location">
                <p>Your location: <strong class="modal-user-location">Loading...</strong></p>
                <div class="click-collect-widget__modal-postcode-input">
                  <input 
                    type="text" 
                    class="click-collect-widget__modal-postcode-field" 
                    placeholder="Enter postcode (e.g. 2000)"
                    maxlength="4"
                    pattern="\\d{4}"
                    aria-label="Enter your postcode to check store availability"
                  >
                  <button class="click-collect-widget__modal-update-btn" type="button" aria-label="Update postcode">
                    <svg class="icon" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation">
                      <g transform="translate(3 3)" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd">
                        <circle cx="7.824" cy="7.824" r="7.824"></circle>
                        <path stroke-linecap="square" d="m13.971 13.971 4.47 4.47"></path>
                      </g>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="click-collect-widget__modal-stores"></div>
            </div>
          </div>
        </div>
      `;
      
      // Append modal to document body
      document.body.insertAdjacentHTML('beforeend', modalHtml);
    }

    /**
     * Close the stores modal
     */
    closeStoresModal() {
      const modal = document.getElementById('click-collect-modal');
      if (modal) {
        modal.remove();
      }
      document.body.style.overflow = '';
    }

    /**
     * Render all stores in the modal
     */
    renderModalStores() {
      const modal = document.getElementById('click-collect-modal');
      const modalStoresContainer = modal?.querySelector('.click-collect-widget__modal-stores');
      if (!modalStoresContainer || !this.allLocations) return;
      
      const preferredStore = JSON.parse(localStorage.getItem('flex_preferred_store') || '{}');
      
      // Sort locations by distance
      const sortedLocations = [...this.allLocations].sort((a, b) => {
        return parseFloat(a.distance_km) - parseFloat(b.distance_km);
      });
      
      modalStoresContainer.innerHTML = sortedLocations.map(location => {
        const stockStatus = this.getStockStatus(location.stock_status);
        const isPreferred = preferredStore.id === location.id;
        
        return `
          <div class="click-collect-widget__modal-store">
            <div class="click-collect-widget__modal-store-info">
              <h4 class="click-collect-widget__modal-store-name">${this.escapeHtml(location.name)}</h4>
              <p class="click-collect-widget__modal-store-address">
                ${this.escapeHtml(location.address.address1)}, ${this.escapeHtml(location.address.city)} (${parseFloat(location.distance_km).toFixed(1)}km away)
              </p>
              ${location.address.phone ? `<p class="click-collect-widget__modal-store-phone"><a href="tel:${location.address.phone}" class="click-collect-widget__phone-link">${this.formatPhoneNumber(location.address.phone)}</a></p>` : ''}
            </div>
            <div class="click-collect-widget__modal-store-status">
              <div class="click-collect-widget__modal-availability">
                <span class="click-collect-widget__modal-availability-label">Click & Collect</span>
                <span class="click-collect-widget__stock-status click-collect-widget__stock-status--${stockStatus.class}">
                  ${stockStatus.text}
                </span>
              </div>
              <button 
                class="click-collect-widget__modal-set-store-btn ${isPreferred ? 'selected' : ''}"
                data-store-id="${location.id}"
                data-store-name="${this.escapeHtml(location.name)}"
                ${isPreferred ? 'disabled' : ''}
              >
                ${isPreferred ? 'Selected' : 'Set as your store'}
              </button>
            </div>
          </div>
        `;
      }).join('');
      
      // Add event listeners to modal set store buttons
      modalStoresContainer.querySelectorAll('.click-collect-widget__modal-set-store-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          this.handleModalSetStore(e);
        });
      });
    }

    /**
     * Handle set store from modal
     */
    handleModalSetStore(event) {
      const storeId = event.target.dataset.storeId;
      const storeName = event.target.dataset.storeName;
      
      if (storeId && storeName) {
        // Save preferred store
        localStorage.setItem('flex_preferred_store', JSON.stringify({
          id: storeId,
          name: storeName,
          timestamp: Date.now()
        }));
        
        // Update modal buttons
        const modal = document.getElementById('click-collect-modal');
        modal?.querySelectorAll('.click-collect-widget__modal-set-store-btn').forEach(btn => {
          if (btn === event.target) {
            btn.textContent = 'Selected';
            btn.disabled = true;
            btn.classList.add('selected');
          } else {
            btn.textContent = 'Set as your store';
            btn.disabled = false;
            btn.classList.remove('selected');
          }
        });
        
        // Update main widget display
        const selectedStore = this.allLocations.find(location => location.id === storeId);
        if (selectedStore) {
          this.renderPrimaryStore(selectedStore, this.allLocations.length > 1);
        }
        
        // Close modal after a short delay
        setTimeout(() => {
          this.closeStoresModal();
        }, 500);
      }
    }

    /**
     * Handle postcode update from modal
     */
    async handleModalPostcodeUpdate() {
      const modal = document.getElementById('click-collect-modal');
      const postcodeInput = modal?.querySelector('.click-collect-widget__modal-postcode-field');
      const newPostcode = postcodeInput?.value?.trim();
      
      if (!newPostcode) {
        this.showError('Please enter a valid postcode');
        return;
      }
      
      // Basic Australian postcode validation
      if (!/^\d{4}$/.test(newPostcode)) {
        this.showError('Please enter a valid 4-digit Australian postcode');
        return;
      }
      
      // Update current postcode
      this.currentPostcode = newPostcode;
      localStorage.setItem('flex_user_postcode', newPostcode);
      
      // Update widget state to ensure main postcode input stays hidden
      this.updateWidgetState('has-location');
      
      // Update main widget postcode input too
      this.updatePostcodeInput(newPostcode);
      
      // Fetch new store data
      if (this.currentVariantId) {
        await this.fetchStoreAvailability();
        
        // Update modal with new data
        if (this.allLocations) {
          const locationEl = modal?.querySelector('.modal-user-location');
          if (locationEl && this.userCoords) {
            locationEl.textContent = `${this.userPostcode} (${this.userCoords.state})`;
          }
          this.renderModalStores();
        }
      }
    }

    /**
     * Get stock icon based on availability
     */
    getStockIcon(stockClass) {
      switch (stockClass) {
        case 'in-stock':
        case 'low-stock':
          return `<svg class="stock-icon stock-icon--available" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20,6 9,17 4,12"></polyline>
          </svg>`;
        case 'out-of-stock':
        default:
          return `<svg class="stock-icon stock-icon--unavailable" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>`;
      }
    }

    /**
     * Get stock status formatting
     */
    getStockStatus(status) {
      // Clean the status text to remove any phone numbers or extra text
      let cleanStatus = status;
      
      // Remove phone numbers in parentheses (e.g., "(+61894562231)")
      cleanStatus = cleanStatus.replace(/\(\+?\d[\d\s\-\(\)]+\)/g, '').trim();
      
      // Remove "Contact store" text if it exists
      cleanStatus = cleanStatus.replace(/\s*-?\s*contact\s+store/gi, '').trim();
      
      const normalizedStatus = cleanStatus.toLowerCase().replace(/\s+/g, '-');
      
      switch (normalizedStatus) {
        case 'in-stock':
        case 'available':
          return { class: 'in-stock', text: 'In Stock' };
        case 'out-of-stock':
        case 'unavailable':
          return { class: 'out-of-stock', text: 'Out of Stock' };
        case 'low-stock':
          return { class: 'low-stock', text: 'Low Stock' };
        default:
          return { class: 'out-of-stock', text: cleanStatus };
      }
    }

    /**
     * Format phone number to Australian standard
     */
    formatPhoneNumber(phone) {
      if (!phone) return '';
      
      // Remove all non-digit characters
      const digits = phone.replace(/\D/g, '');
      
      // Handle Australian mobile numbers (starting with 61)
      if (digits.startsWith('61') && digits.length >= 10) {
        const number = digits.substring(2); // Remove country code
        
        // Format based on number type
        if (number.startsWith('4')) {
          // Mobile: +61 4XX XXX XXX
          return `+61 ${number.substring(0, 1)} ${number.substring(1, 5)} ${number.substring(5)}`;
        } else if (number.length === 9) {
          // Landline: +61 X XXXX XXXX
          return `+61 ${number.substring(0, 1)} ${number.substring(1, 5)} ${number.substring(5)}`;
        }
      }
      
      // Handle numbers already starting with +61
      if (phone.startsWith('+61')) {
        const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
        const digits = cleanPhone.substring(3); // Remove +61
        
        if (digits.startsWith('4')) {
          // Mobile: +61 4XX XXX XXX
          return `+61 ${digits.substring(0, 1)} ${digits.substring(1, 5)} ${digits.substring(5)}`;
        } else if (digits.length === 9) {
          // Landline: +61 X XXXX XXXX
          return `+61 ${digits.substring(0, 1)} ${digits.substring(1, 5)} ${digits.substring(5)}`;
        }
      }
      
      // Fallback: return original if can't format
      return phone;
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Initial render of the widget structure
     */
    render() {
      this.innerHTML = `
        <div class="click-collect-widget__header">
          <h3 class="click-collect-widget__title">Check Stock In-Store</h3>
          <div class="click-collect-widget__header-actions"></div>
        </div>
        
        <div class="click-collect-widget__location-input">
          <input 
            type="text" 
            class="click-collect-widget__postcode-input" 
            placeholder="Enter postcode (e.g. 4000)"
            maxlength="4"
            pattern="\\d{4}"
            aria-label="Enter your postcode to check store availability"
          >
          <button class="click-collect-widget__update-btn" type="button" aria-label="Check availability">
            <svg class="icon" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation">
              <g transform="translate(3 3)" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd">
                <circle cx="7.824" cy="7.824" r="7.824"></circle>
                <path stroke-linecap="square" d="m13.971 13.971 4.47 4.47"></path>
              </g>
            </svg>
          </button>
        </div>
        
        
        <div class="click-collect-widget__loading">
          <div class="click-collect-widget__spinner" aria-hidden="true"></div>
          <span>Checking store availability...</span>
        </div>
        
        <div class="click-collect-widget__main-store"></div>
        
        <div class="click-collect-widget__error" role="alert" aria-live="polite"></div>
      `;
    }
  }

  customElements.define('click-collect-widget', ClickCollectWidget);
}
